class PersistentCache {
  constructor(prefix = 'cc_cache_', defaultTTL = 30 * 60 * 1000) {
    this.prefix = prefix;
    this.defaultTTL = defaultTTL;
    this.inFlight = new Map();
    this.apiCache = new Map();
  }

  _getKey(key) {
    return `${this.prefix}${key}`;
  }

  _isExpired(entry) {
    if (!entry?.expiry) return true;
    return Date.now() > entry.expiry;
  }

  async get(key, fetcher, options = {}) {
    const {
      ttl = this.defaultTTL,
      staleWhileRevalidate = true,
      forceRefresh = false
    } = options;

    const cacheKey = this._getKey(key);
    const now = Date.now();

    if (!forceRefresh && this.apiCache?.has(key)) {
      const entry = this.apiCache.get(key);
      if (entry?.expiry && Date.now() < entry.expiry) {
        return entry.data;
      }
      this.apiCache.delete(key);
    }

    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const entry = JSON.parse(raw);

        if (!this._isExpired(entry)) {
          if (staleWhileRevalidate && entry.expiry < now + ttl * 0.5) {
            this._revalidateInBackground(key, fetcher, ttl);
          }
          return entry.data;
        }

        if (staleWhileRevalidate) {
          this._revalidateInBackground(key, fetcher, ttl);
          return entry.data;
        }

        sessionStorage.removeItem(cacheKey);
      }
    } catch (e) {
      console.warn(`[Cache] Failed to read ${cacheKey}:`, e);
      sessionStorage.removeItem(cacheKey);
    }

    if (this.inFlight.has(key)) {
      return this.inFlight.get(key);
    }

    const request = (async () => {
      try {
        const data = await fetcher();
        this._set(cacheKey, data, ttl);
        return data;
      } catch (error) {
        try {
          const raw = sessionStorage.getItem(cacheKey);
          if (raw) {
            const entry = JSON.parse(raw);
            console.warn(`[Cache] Serving stale data after error for ${key}`);
            return entry.data;
          }
        } catch { }
        throw error;
      } finally {
        this.inFlight.delete(key);
      }
    })();

    this.inFlight.set(key, request);
    return request;
  }

  _set(key, data, ttl) {
    if (typeof this.apiCache !== 'undefined' && this.apiCache?.set) {
      const cleanKey = key.replace(this.prefix, '');
      this.apiCache.set(cleanKey, {
        data,
        expiry: Date.now() + ttl,
      });
    }

    try {
      const entry = {
        data,
        expiry: Date.now() + ttl,
        timestamp: Date.now()
      };
      sessionStorage.setItem(key, JSON.stringify(entry));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        this._cleanupOldEntries();
        try {
          sessionStorage.setItem(key, JSON.stringify({
            data,
            expiry: Date.now() + ttl,
            timestamp: Date.now()
          }));
        } catch { }
      }
      console.warn(`[Cache] Failed to write ${key}:`, e);
    }
  }

  async _revalidateInBackground(key, fetcher, ttl) {
    setTimeout(async () => {
      try {
        const data = await fetcher();
        this._set(this._getKey(key), data, ttl);
      } catch (e) {
        console.warn(`[Cache] Background revalidation failed for ${key}:`, e);
      }
    }, 0);
  }

  _cleanupOldEntries() {
    const now = Date.now();
    const keysToRemove = [];

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(this.prefix)) {
        try {
          const entry = JSON.parse(sessionStorage.getItem(key));
          if (this._isExpired(entry) || (now - entry.timestamp) > 7 * 24 * 60 * 60 * 1000) {
            keysToRemove.push(key);
          }
        } catch { }
      }
    }

    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }

  clear(prefix = null) {
    const targetPrefix = prefix ? this._getKey(prefix) : this.prefix;
    const keysToRemove = [];

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(targetPrefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => sessionStorage.removeItem(key));

    if (typeof this.apiCache !== 'undefined' && this.apiCache?.clear) {
      if (prefix) {
        for (const key of this.apiCache.keys()) {
          if (key.startsWith(prefix)) this.apiCache.delete(key);
        }
      } else {
        this.apiCache.clear();
      }
    }
  }
}

window.ccCache = new PersistentCache('cc_cache_');